import React, { useMemo } from 'react';

import { useDispatch, useSelector } from 'editor/src/store/hooks';
import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import getECommercePageCountOptionIndex from 'editor/src/store/variants/helpers/getECommercePageCountOptionIndex';
import setECommerceProductIdOperation from 'editor/src/store/variants/operation/setECommerceProductIdOperation';
import getECommerceProduct from 'editor/src/store/variants/selector/getECommerceProduct';

import ECommerceProductControl from './ECommerceProductControl';
import { getFormattedVariantOptions, getSelectedVariant } from './eCommerceVariantUtils';

function ECommerceProductContent() {
  const eCommerceProduct = useSelector(getECommerceProduct);
  const pageCountOptionsEnabled = useSelector(isPageCountOptionsEnabled);
  const dispatch = useDispatch();

  const selectedVariant = useMemo(
    () =>
      eCommerceProduct &&
      eCommerceProduct.variants.find((variant) => variant.id === eCommerceProduct?.selectedVariantId),
    [eCommerceProduct],
  );

  const formattedVariantControls = useMemo(() => {
    // One variant leaves nothing to switch to; the Product tab can still be open for the quantity stepper.
    if (!eCommerceProduct?.options || !selectedVariant || eCommerceProduct.variants.length < 2) {
      return null;
    }

    return getFormattedVariantOptions(eCommerceProduct.options, eCommerceProduct.variants, selectedVariant);
  }, [eCommerceProduct, selectedVariant]);

  const onChange = (controlIndex: number, value: string) => {
    if (!eCommerceProduct || !selectedVariant) {
      return;
    }

    const newVariant = getSelectedVariant(eCommerceProduct.variants, selectedVariant, controlIndex, value);
    if (!newVariant) {
      return;
    }

    dispatch(setECommerceProductIdOperation(newVariant.id));
  };

  if (!formattedVariantControls) {
    return null;
  }

  // The page counts fold into a dropdown: a book sells dozens of them, and a wall of numbered
  // buttons crowds the tab. Only while the host enables page counts, so other hosts keep buttons.
  const pageCountIndex =
    pageCountOptionsEnabled && eCommerceProduct ? getECommercePageCountOptionIndex(eCommerceProduct) : -1;

  return (
    <div>
      {selectedVariant &&
        formattedVariantControls.map(
          ({ name, hidden, values }, i) =>
            !hidden && (
              <ECommerceProductControl
                key={name}
                title={name}
                options={values}
                selectedValue={selectedVariant.optionValues[i]}
                onChange={(value) => onChange(i, value)}
                dropdown={i === pageCountIndex}
              />
            ),
        )}
    </div>
  );
}

export default React.memo(ECommerceProductContent);
